/**
 * 기사 인증 컨텍스트 — 휴대폰 알림톡 OTP → Firebase custom token.
 *
 * docuservice 의 같은 흐름을 businweb 에 맞춘 것. **다른 점은 저장소뿐**:
 * docuservice 는 Firestore 로 기사 프로필·세션을 실시간 구독하지만 businweb 은 Cloud SQL 이라
 * 구독이 없다. 그래서
 *  - 프로필은 로그인 직후 `getDriverMe` 로 한 번 불러온다.
 *  - 단일 세션 강제는 **서버가 매 호출마다** 대조한다(`requireDriver`). 다른 기기 로그인으로
 *    밀리면 다음 API 호출이 `permission-denied` 로 떨어지고 `driverApi` 가 로그아웃시킨다.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  browserSessionPersistence, onAuthStateChanged, setPersistence, signInWithCustomToken, signOut, type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import { driverApi, EVICTED_KEY, setActingDriverId, type DriverAlloc, type DriverProfile, type FleetDriver } from './driverApi';
import { clearDriverCache } from './driverCache';

interface DriverAuthValue {
  user: User | null;
  driver: DriverProfile | null;
  allocs: DriverAlloc[];
  /** 차주이기도 한가 — 홈의 「예비기사 분배」 타일. */
  isOwner: boolean;
  /** 기사 앱 신청 스위치 — 켜지면 홈 세금계산서 타일이 앱 안 신청 화면으로 간다. */
  orderOpen: boolean;
  /** 차주면 그 차량의 다른 기사(#258) — 비면 「누구 것으로」 고르기가 안 보인다. */
  fleet: FleetDriver[];
  /** 지금 고른 기사(null = 본인). 명세서·세금계산서·4종 신청이 이 사람 것으로 열린다. */
  acting: number | null;
  setActing: (id: number | null) => void;
  loading: boolean;
  /** 다른 기기 로그인으로 밀려났을 때 로그인 화면에 띄울 문구. */
  evictedReason: string | null;
  /** 관리자 「기사앱으로 보기」(읽기 전용) — 아니면 null. until = 끝나는 시각(ms). */
  viewAs: { by: string; until: number } | null;
  requestOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<DriverAuthValue | null>(null);

/** 직전 로그인 uid — 바뀌면 목록 캐시를 비운다. */
let lastUid: string | null = null;

/**
 * 관리자 「기사앱으로 보기」 — 담당자 화면이 `#viewas=<custom token>` 로 연다(functions `adminViewAsDriver`).
 * 🔴 이 탭에서만 로그인(sessionPersistence) — 탭을 닫으면 끝나고, 같은 폰·PC 의 다른 기사앱 창은 그대로.
 * 토큰은 주소에서 바로 지운다(뒤로 가기·복사로 남지 않게).
 */
const viewAsToken: string | null = (() => {
  if (typeof window === 'undefined') return null;
  const m = /^#viewas=(.+)$/.exec(window.location.hash);
  if (!m) return null;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return decodeURIComponent(m[1] ?? '');
})();

export function DriverAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [allocs, setAllocs] = useState<DriverAlloc[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [fleet, setFleet] = useState<FleetDriver[]>([]);
  const [acting, setActingState] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const setActing = useCallback((id: number | null) => {
    setActingDriverId(id);
    setActingState(id);
  }, []);
  const [evictedReason, setEvictedReason] = useState<string | null>(null);
  const [viewAs, setViewAs] = useState<{ by: string; until: number } | null>(null);
  // 관리자 보기 토큰으로 들어왔으면 로그인 판정 전에 그 토큰으로 바꿔 탄다.
  const [viewAsPending, setViewAsPending] = useState(viewAsToken !== null);
  useEffect(() => {
    if (!viewAsToken) return;
    setPersistence(auth, browserSessionPersistence)
      .then(() => signInWithCustomToken(auth, viewAsToken))
      .catch(() => setEvictedReason('관리자 보기 링크가 만료됐습니다. 담당자 화면에서 다시 열어 주세요.'))
      .finally(() => setViewAsPending(false));
  }, []);

  useEffect(() => {
    if (!user) { setViewAs(null); return; }
    let cancelled = false;
    user.getIdTokenResult().then((r) => {
      if (cancelled) return;
      const c = r.claims as { viewAs?: boolean; viewAsBy?: string; viewAsUntil?: number };
      setViewAs(c.viewAs ? { by: c.viewAsBy ?? '', until: Number(c.viewAsUntil ?? 0) } : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        // 기사 토큰이 아닌 세션(담당자 Google 로그인 등)은 기사앱에서 무시한다.
        // 사람이 바뀌면(로그아웃·다른 기사) 받아 둔 목록을 버린다 — 남의 명세가 한 순간도 보이지 않게.
        const changed = (u?.uid ?? null) !== lastUid;
        if (changed) clearDriverCache();
        lastUid = u?.uid ?? null;
        setUser(u);
        // 🔴 다른 기사로 바뀔 때도 비운다(11-02 way 「직원_회계팀에 예비기사 분배가 보인다」) — 로그아웃 때만 비우면
        //    관리자가 차주 화면 → 다른 기사 화면으로 넘어갈 때 getMe 가 실패하면 앞 사람의 타일(차주·세금계산서)이 남는다.
        if (!u || changed) {
          setDriver(null);
          setAllocs([]);
          setIsOwner(false);
          setOrderOpen(false);
          setFleet([]);
          setActingDriverId(null);
          setActingState(null);
        }
        if (!u) {
          const reason = sessionStorage.getItem(EVICTED_KEY);
          if (reason) {
            setEvictedReason(reason);
            sessionStorage.removeItem(EVICTED_KEY);
          }
        }
        setLoading(false);
      }),
    [],
  );

  // 로그인되면 본인 프로필·편성을 한 번 불러온다.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    driverApi
      .getMe()
      .then((res) => {
        if (cancelled) return;
        setDriver(res.driver);
        setAllocs(res.allocs ?? []);
        setIsOwner(res.isOwner === true);
        setOrderOpen(res.orderOpen === true);
        setFleet(res.fleet ?? []);
      })
      .catch(() => {
        // permission-denied 면 driverApi 가 이미 로그아웃시킨다. 다른 오류는 프로필만 빈 채로 둔다.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const requestOtp = useCallback(async (phone: string) => {
    const fn = httpsCallable<{ phone: string }, { sent: boolean }>(functions, 'requestOtp');
    await fn({ phone });
  }, []);

  const verifyOtp = useCallback(async (phone: string, code: string) => {
    const fn = httpsCallable<{ phone: string; code: string }, { token: string; sessionId: string }>(
      functions,
      'verifyOtp',
    );
    const { data } = await fn({ phone, code });
    setEvictedReason(null);
    await signInWithCustomToken(auth, data.token);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const busy = loading || viewAsPending;
  const value = useMemo<DriverAuthValue>(
    () => ({ user, driver, allocs, isOwner, orderOpen, fleet, acting, setActing, loading: busy, evictedReason, viewAs, requestOtp, verifyOtp, logout }),
    [user, driver, allocs, isOwner, orderOpen, fleet, acting, setActing, busy, evictedReason, viewAs, requestOtp, verifyOtp, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDriverAuth(): DriverAuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDriverAuth 는 DriverAuthProvider 안에서만 쓸 수 있습니다.');
  return v;
}
